import React, { useEffect, useMemo, useState } from "react";
import { api, money } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { TrendingUp, ReceiptText, Package, Loader2, Download, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const num = (v) => Number(v || 0);

function dateKey(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function AnalyticsPage() {
  const [orders, setOrders] = useState([]);
  const [orderItems, setOrderItems] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [appliedStart, setAppliedStart] = useState("");
  const [appliedEnd, setAppliedEnd] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/gsheet/orders"),
      api.get("/gsheet/orderItems"),
      api.get("/gsheet/catalog"),
    ])
      .then(([ordersRes, itemsRes, catalogRes]) => {
        setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
        setOrderItems(Array.isArray(itemsRes.data) ? itemsRes.data : []);
        setCatalog(Array.isArray(catalogRes.data) ? catalogRes.data : []);
        setAppliedStart(start);
        setAppliedEnd(end);
      })
      .catch((err) => {
        const detail = err.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "No se pudo cargar Analytics.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const data = useMemo(() => {
    const validOrders = orders.filter((o) => {
      if (!String(o.order_id || "").trim()) return false;
      if (String(o.order_status || "").toLowerCase() === "cancelada") return false;
      const d = dateKey(o.order_date);
      if (appliedStart && (!d || d < appliedStart)) return false;
      if (appliedEnd && (!d || d > appliedEnd)) return false;
      return true;
    });

    const validIds = new Set(validOrders.map((o) => String(o.order_id)));
    const items = orderItems.filter((it) => validIds.has(String(it.order_id)));

    const totalRevenue = validOrders.reduce((sum, o) => sum + num(o.total_amount), 0);
    const itemsSold = items.reduce((sum, it) => sum + num(it.qty), 0);

    const byProduct = {};
    items.forEach((it) => {
      const key = String(it.product_id || it.product_name || "Producto");
      if (!byProduct[key]) {
        byProduct[key] = {
          name: it.product_name || it.product_id || "Producto",
          qty: 0,
          revenue: 0,
        };
      }
      byProduct[key].qty += num(it.qty);
      byProduct[key].revenue += num(it.line_total);
    });

    const topItems = Object.values(byProduct)
      .sort((a, b) => b.qty - a.qty || b.revenue - a.revenue)
      .slice(0, 8);

    const lowStock = catalog
      .filter((it) => String(it.product_id || "").trim())
      .map((it) => ({
        name: it.product_name || [it.genero, it.especie, it.cultivar].filter(Boolean).join(" ") || it.product_id,
        stock: num(it.stock_qty),
        status: it.status || "",
      }))
      .filter((it) => it.stock <= 1 || String(it.status).toLowerCase() === "no disponible")
      .sort((a, b) => a.stock - b.stock);

    return {
      total_revenue: totalRevenue,
      total_bills: validOrders.length,
      items_sold: itemsSold,
      top_items: topItems,
      low_stock: lowStock,
      valid_orders: validOrders,
    };
  }, [orders, orderItems, catalog, appliedStart, appliedEnd]);

  const applyRange = () => {
    setAppliedStart(start);
    setAppliedEnd(end);
  };

  const exportCsv = () => {
    try {
      const header = [
        "Order ID", "Date", "Customer", "Phone", "Order Status", "Payment Status",
        "Payment Method", "Delivery Type", "Subtotal", "Discount", "Shipping", "IVA", "Total", "Balance"
      ];
      const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const rows = data.valid_orders.map((o) => [
        o.order_id,
        o.order_date,
        o.order_customer_name,
        o.customer_phone,
        o.order_status,
        o.payment_status,
        o.payment_method,
        o.delivery_type,
        num(o.subtotal_amount),
        num(o.discount_amount),
        num(o.shipping_amount),
        num(o.iva_amount),
        num(o.total_amount),
        num(o.balance_due),
      ]);
      const csv = [header, ...rows].map((row) => row.map(escape).join(",")).join("\n");
      const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sales_report.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const cards = [
    { label: "Total Revenue", value: money(data.total_revenue), icon: TrendingUp },
    { label: "Total Bills", value: data.total_bills, icon: ReceiptText },
    { label: "Items Sold", value: data.items_sold, icon: Package },
  ];

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6" data-testid="analytics-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <h1 className="font-display text-2xl font-extrabold text-foreground">Sales Reports</h1>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="space-y-1">
            <Label className="text-xs">From</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="report-start-date" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">To</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="report-end-date" />
          </div>
          <Button variant="outline" onClick={applyRange} disabled={loading} data-testid="apply-range-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
          </Button>
          <Button onClick={exportCsv} className="gap-1.5" data-testid="export-csv-button">
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {cards.map((c) => (
          <div key={c.label} className="bg-card rounded-2xl border border-border p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{c.label}</span>
              <c.icon className="h-5 w-5 text-accent" />
            </div>
            <p className="font-mono font-bold text-3xl text-foreground mt-2">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-2xl border border-border p-6 shadow-sm">
          <h2 className="font-display text-lg font-bold text-foreground mb-4">Best Sellers</h2>
          {data.top_items.length === 0 ? (
            <p className="text-muted-foreground text-sm">No sales in this range.</p>
          ) : (
            <div className="space-y-3">
              {data.top_items.map((t, i) => {
                const max = data.top_items[0].qty || 1;
                return (
                  <div key={`${t.name}-${i}`}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-foreground">{i + 1}. {t.name}</span>
                      <span className="font-mono text-muted-foreground">{t.qty} · {money(t.revenue)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full bg-accent rounded-full" style={{ width: `${(t.qty / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-card rounded-2xl border border-border p-6 shadow-sm" data-testid="low-stock-panel">
          <h2 className="font-display text-lg font-bold text-foreground mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" /> Low Stock Alerts
          </h2>
          {data.low_stock.length === 0 ? (
            <p className="text-muted-foreground text-sm">All items are well stocked.</p>
          ) : (
            <div className="space-y-2">
              {data.low_stock.map((s, i) => (
                <div key={`${s.name}-${i}`} className="flex items-center justify-between p-2.5 rounded-lg bg-secondary/60">
                  <span className="font-medium text-foreground text-sm">{s.name}</span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${s.stock <= 0 ? "bg-destructive/15 text-destructive" : "bg-amber-500/20 text-amber-600 dark:text-amber-400"}`}>
                    {s.stock <= 0 ? "Out of stock" : "Only 1 left"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
